/**
 * TornPaperDivider — reusable CSS/SVG separator that sits between a dark
 * section (e.g. the Hero) and a light content section below it.
 * The jagged path is generated once (not baked into any image) with a
 * seeded pseudo-random irregular edge, a soft drop shadow for depth, thin
 * paper-fiber ticks, and a faint red brand-accent rim - so it reads as a
 * real hand-torn edge instead of a flat mechanical zigzag.
 */
const TORN_HEIGHT = 40;
const TORN_WIDTH = 1200;
const TEETH = 60;

// Deterministic pseudo-random (same jagged shape on every render, no external deps)
const seededRandom = (seed) => {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
};

const buildTornPoints = () => {
  const rand = seededRandom(42);
  const step = TORN_WIDTH / TEETH;
  const points = [[0, TORN_HEIGHT * 0.5]];
  for (let i = 1; i <= TEETH; i++) {
    const jitterX = (rand() - 0.5) * step * 0.5;
    const x = Math.min(TORN_WIDTH, Math.max(0, i * step + jitterX));
    const depth = 0.12 + rand() * 0.8;
    points.push([x, TORN_HEIGHT * depth]);
  }
  return points;
};

const buildFibers = (points) => {
  const rand = seededRandom(99);
  const fibers = [];
  for (let i = 0; i < 45; i++) {
    const idx = Math.floor(rand() * (points.length - 1));
    const [x, y] = points[idx];
    fibers.push({
      x: x + (rand() - 0.5) * 8,
      y,
      dx: (rand() - 0.5) * 4,
      len: 2 + rand() * 6,
      w: 0.5 + rand() * 0.8,
      o: 0.15 + rand() * 0.35,
    });
  }
  return fibers;
};

const linePath = (points) =>
  points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');

const TORN_POINTS = buildTornPoints();
const TORN_LINE_PATH = linePath(TORN_POINTS);
const TORN_FILL_PATH = `${TORN_LINE_PATH} L${TORN_WIDTH},${TORN_HEIGHT} L0,${TORN_HEIGHT} Z`;
const FIBERS = buildFibers(TORN_POINTS);

export const TornPaperDivider = ({ bgColor = 'bg-black', fillColor = '#F9FAFB' }) => (
  <div className={`relative w-full ${bgColor}`} data-testid="torn-paper-divider">
    <svg
      viewBox={`0 0 ${TORN_WIDTH} ${TORN_HEIGHT}`}
      preserveAspectRatio="none"
      className="w-full h-8 sm:h-14 block"
    >
      <defs>
        {/* Soft shadow the raised torn edge casts DOWN onto the light section below */}
        <linearGradient id="tornShadowGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#000000" stopOpacity="0.55" />
          <stop offset="10%" stopColor="#000000" stopOpacity="0.22" />
          <stop offset="28%" stopColor="#000000" stopOpacity="0" />
        </linearGradient>
        {/* Thin light catching the raised lip of each torn peak */}
        <linearGradient id="tornHighlightGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Main torn paper fill */}
      <path fill={fillColor} d={TORN_FILL_PATH} />

      {/* Cast shadow just below the tear, fading out - gives the edge depth/lift */}
      <path fill="url(#tornShadowGradient)" d={TORN_FILL_PATH} />

      {/* Bright highlight tracing the raised lip of the tear */}
      <path fill="none" stroke="url(#tornHighlightGradient)" strokeWidth="2" d={TORN_LINE_PATH} />

      {/* Faint red brand-accent rim tracing the tear line */}
      <path fill="none" stroke="#CA8A04" strokeWidth="1" strokeOpacity="0.45" d={TORN_LINE_PATH} />

      {/* Torn paper fibers poking up from the edge */}
      {FIBERS.map((f, idx) => (
        <line
          key={idx}
          x1={f.x}
          y1={f.y}
          x2={f.x + f.dx}
          y2={f.y - f.len}
          stroke={fillColor}
          strokeWidth={f.w}
          strokeOpacity={f.o}
        />
      ))}
    </svg>
  </div>
);

export default TornPaperDivider;
